import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { cloneDeep } from 'lodash'
import { Col, Divider, Row, Space, Switch } from 'antd'
import { PrimaryButton } from 'Components/button'
import { DefaultStatus, menuBankAccount, menuSupplierPaymentTypes } from 'helpers/constanst'
import {
  isFormRequired,
  isFormError,
  formValidation,
  getDefaultStatus,
  parseNumberToInt,
} from 'helpers/format'
import { CreateSupplierReq, SupplierDetailReq, UpdateSupplierReq } from 'Store/supplier/types'
import { createSupplierAPI, getSupplierDetailAPI, updateSupplierAPI } from 'Store/supplier/api'
import {
  selectSupplierActionEvent,
  selectSupplierDetail,
  selectSupplierDetailEvent,
} from 'Store/supplier/selector'
import { selectCurrentUser } from 'Store/user/selector'
import { LabelItem, AppInput, AppSelect, UploadForm, AppTextArea } from 'Components/form'
import EventModal from 'Components/modal/EventModal'
import { clearEventAction } from 'Store/supplier/actions'
import LoadingView from 'Components/modal/LoadingView'
import { DetailDrawer } from 'Components/common/MainLayout'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'

type Props = ReduxProps & {
  visible: boolean
  onCloseDrawer: () => void
  onSuccess?: () => void
  mode: any
  id?: number
}

const SuppliersDetail: React.FunctionComponent<Props> = props => {
  const {
    onCloseDrawer,
    visible,
    currentUser,
    event,
    onSuccess = () => {},
    id = 0,
    mode,
    detail,
    detailEvent,
  } = props
  const isCreateMode = mode === 'create'
  const requireFeild = [
    'supplier_code',
    'name',
    //'tax_payer_id',
    'address',
    'payment_period',
    'account_name',
    'account_no',
    'account_bank',
    'account_branch',
    //'account_book_bank',
    'mobile_no',
    'payment_type',
  ]
  const initForm = {
    account_bank: '',
    account_book_bank: '',
    account_branch: '',
    account_name: '',
    account_no: '',
    address: '',
    email: '',
    merchant_id: currentUser?.activeMerchant?.id || 0,
    mobile_no: '',
    name: '',
    payment_period: undefined,
    payment_type: undefined,
    phone_no: '',
    status: DefaultStatus.ACTIVE,
    supplier_code: '',
    tax_payer_id: '',
  } as CreateSupplierReq
  const [formData, setFormData] = useState(cloneDeep(initForm))
  const [formError, setFormError] = useState([] as string[])
  const dispatch = useDispatch()

  const validateForm = () => {
    const error = formValidation(requireFeild, formData)
    setFormError([...error])
    return error.length > 0 ? false : true
  }

  const onCreate = (): void => {
    if (!validateForm()) return
    const req: CreateSupplierReq = {
      ...formData,
    }
    dispatch(createSupplierAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const onGetDetail = (): void => {
    const req: SupplierDetailReq = {
      id,
    }
    dispatch(getSupplierDetailAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const onUpdate = (): void => {
    if (!validateForm()) return
    const req: UpdateSupplierReq = {
      ...formData,
      id,
    }
    dispatch(updateSupplierAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  useEffect(() => {
    // clear form data when user cloase drawer
    if (!visible) {
      setFormData(cloneDeep(initForm))
      setFormError([])
    }

    // get detail on "edit" mode
    if (visible && !isCreateMode && id > 0) {
      onGetDetail()
    }
  }, [visible])

  useEffect(() => {
    if (mode === 'edit' && id > 0) {
      const form = {
        ...detail,
      } as CreateSupplierReq
      setFormData({ ...form })
    }
  }, [detail])

  useEffect(() => {
    if (formError.length > 0) {
      validateForm()
    }
  }, [formData])

  const onUpdateData = (val: string, key: string) => {
    if (key === 'tax_payer_id' && val.length > 13) {
      return
    }
    setFormData({
      ...formData,
      [key]: val,
    })
  }

  const handleToggleStatus = (val: boolean) => {
    setFormData({
      ...formData,
      status: val ? DefaultStatus.ACTIVE : DefaultStatus.INACTIVE,
    })
  }

  const clearEvent = (): void => {
    dispatch(clearEventAction())
  }

  const onEventSuccess = (): void => {
    clearEvent()
    onSuccess()
  }

  const isRequired = (name: string) => {
    return isFormRequired(requireFeild, name)
  }

  const isError = (name: string) => {
    return isFormError(formError, name)
  }

  const isPublic = formData.status === DefaultStatus.ACTIVE
  const title = isCreateMode ? 'เพิ่มข้อมูลบริษัท/Supplier' : 'แก้ไขข้อมูลบริษัท/Supplier'

  const FooterContent = (
    <Space size="middle" align="baseline">
      <PrimaryButton text="ยกเลิก" btnType="Cancel" onClick={onCloseDrawer} />
      {isCreateMode ? (
        <PrimaryButton text="ยืนยันการสร้าง" onClick={onCreate} />
      ) : (
        <PrimaryButton text="ยืนยันการแก้ไขข้อมูล" onClick={onUpdate} />
      )}
    </Space>
  )

  return (
    <DetailDrawer title={title} onCloseDrawer={onCloseDrawer} visible={visible} footer={FooterContent}>
      <LoadingView isShow={detailEvent.IsLoading} />
      <EventModal event={[event]} enable={true} successCalback={onEventSuccess} errorCalback={clearEvent} />
      <Row justify="space-between" gutter={16}>
        <Col md={12} xs={24}>
          <AppInput
            name="supplier_code"
            label="รหัสบริษัท"
            isRequired={isRequired('supplier_code')}
            isError={isError('supplier_code')}
            value={formData.supplier_code}
            onChange={onUpdateData}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="name"
            label="ชื่อบริษัท/Supplier"
            isRequired={isRequired('name')}
            isError={isError('name')}
            value={formData.name}
            onChange={onUpdateData}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="tax_payer_id"
            label="เลขประจำตัวผู้เสียภาษี"
            isRequired={isRequired('tax_payer_id')}
            isError={isError('tax_payer_id')}
            value={formData.tax_payer_id}
            onChange={onUpdateData}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="mobile_no"
            label="เบอร์โทรศัพท์มือถือ"
            isRequired={isRequired('mobile_no')}
            isError={isError('mobile_no')}
            value={formData.mobile_no}
            onChange={onUpdateData}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="phone_no"
            label="เบอร์โทรศัพท์"
            isRequired={isRequired('phone_no')}
            isError={isError('phone_no')}
            value={formData.phone_no}
            onChange={onUpdateData}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="email"
            label="อีเมล"
            isRequired={isRequired('email')}
            isError={isError('email')}
            value={formData.email}
            onChange={onUpdateData}
          />
        </Col>
        <Col md={24} xs={24}>
          <AppTextArea
            label="ที่อยู่บริษัท"
            isRequired={isRequired('address')}
            isError={isError('address')}
            name="address"
            rows={3}
            value={formData.address}
            onChange={onUpdateData}
          />
        </Col>
        <Divider />
        <Col md={24} xs={24}>
          <Title>ข้อมูลการชำระเงิน</Title>
          <br />
        </Col>
        <Col md={12} xs={24}>
          <AppSelect
            name="account_bank"
            label="ธนาคาร"
            isRequired={isRequired('account_bank')}
            isError={isError('account_bank')}
            value={formData.account_bank}
            onChange={onUpdateData}
            menu={menuBankAccount}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="account_branch"
            label="สาขา"
            isRequired={isRequired('account_branch')}
            isError={isError('account_branch')}
            value={formData.account_branch}
            onChange={onUpdateData}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="account_name"
            label="ชื่อบัญชี"
            isRequired={isRequired('account_name')}
            isError={isError('account_name')}
            value={formData.account_name}
            onChange={onUpdateData}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="account_no"
            label="เลขที่บัญชี"
            isRequired={isRequired('account_no')}
            isError={isError('account_no')}
            value={formData.account_no}
            onChange={onUpdateData}
          />
        </Col>

        <Col md={12} xs={24}>
          <AppSelect
            name="payment_type"
            label="รูปแบบการชำระเงิน"
            isRequired={isRequired('payment_type')}
            isError={isError('payment_type')}
            value={formData.payment_type}
            onChange={onUpdateData}
            menu={menuSupplierPaymentTypes}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="payment_period"
            label="ระยะเวลาชำระเงิน"
            type="number"
            isRequired={isRequired('payment_period')}
            isError={isError('payment_period')}
            value={formData.payment_period ? parseNumberToInt(formData.payment_period) : ''}
            onChange={onUpdateData}
            resProps={{ suffix: 'วัน' }}
          />
        </Col>
        <Col md={24} xs={24}>
          <UploadForm
            token={currentUser.accessToken}
            onUpdateData={onUpdateData}
            currentFile={formData.account_book_bank}
            folder="supplier-account"
            listType="text"
            isPrivate={true}
            name="account_book_bank"
            description="ขนาดไม่เกิน 5MB ต่อไฟล์ (.png .jpg .pdf)"
            isRequired={isRequired('account_book_bank')}
            isError={isError('account_book_bank')}
            label="ไฟล์หน้าบัญชี"
          />
        </Col>
        <Col md={24} xs={24}>
          <br />
          <Switch checked={isPublic} onChange={handleToggleStatus} />
          &nbsp;
          <LabelItem label={`สถานะ : ${getDefaultStatus(formData.status)}`} />
        </Col>
      </Row>
    </DetailDrawer>
  )
}

const Title = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    event: selectSupplierActionEvent(state),
    detail: selectSupplierDetail(state),
    detailEvent: selectSupplierDetailEvent(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(SuppliersDetail)
